// ThemePanel — the document designer, for the CompanyDetail tab.
//
// The complaint this answers: "the templates are really obscure and there is no
// way to design them." A template used to be a key in a dropdown, and a
// merchant's document was byte-identical to every other merchant's apart from
// the issuer block.
//
// TWO THINGS THIS PANEL MUST SAY OUT LOUD, because both are invisible and both
// lead an operator to a wrong belief they will act on:
//
//   1. Saving here does NOT change documents already issued. The theme is
//      frozen onto each document at issue, exactly like the issuer block and
//      the payment trail — a merchant who rebrands does not rewrite a receipt
//      someone already filed with their taxes. An operator who thinks otherwise
//      will "fix" a typo here and tell a customer their receipt is corrected.
//   2. Hiding is presentational. A hidden bank block is still recorded on the
//      document; it is simply not printed. An operator who reads "hide" as
//      "do not collect" will make a compliance decision on a UI label.
//
// The toggles are HIDE at every layer down to the column — see api/theme.ts. The
// checkboxes here read as "Show …" because that is how an operator thinks, and
// the inversion happens AT THE CHECKBOX and nowhere else. One negation, in one
// place, that you can see next to the label it belongs to.
import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Pill } from "@/components/UI";
import { apiErrorMessage } from "@/utils/apiError";
import { useUI } from "@/store/ui";
import {
  getTheme,
  putTheme,
  isValidAccentColor,
  EMPTY_THEME,
  ACCENT_PRESETS,
  MAX_THEME_TITLE_LEN,
  MAX_THEME_SHORT_TEXT_LEN,
  MAX_THEME_TERMS_LEN,
  type DocumentTheme,
} from "@/api/theme";

export function ThemePanel({ companyId }: { companyId: string }) {
  const qc = useQueryClient();
  const addToast = useUI((s) => s.addToast);

  const themeQ = useQuery({
    queryKey: ["theme", companyId],
    queryFn: () => getTheme(companyId),
  });

  const [form, setForm] = useState<DocumentTheme>(EMPTY_THEME);
  useEffect(() => {
    if (themeQ.data) setForm(themeQ.data);
  }, [themeQ.data]);

  const save = useMutation({
    mutationFn: (t: DocumentTheme) => putTheme(companyId, t),
    onSuccess: (saved) => {
      setForm(saved);
      qc.setQueryData(["theme", companyId], saved);
      // The preview renders from the LIVE theme, so it is now stale.
      void qc.invalidateQueries({ queryKey: ["preview", companyId] });
      addToast({ title: "Branding saved", variant: "success" });
    },
    onError: (e) =>
      addToast({
        title: "Could not save branding",
        body: apiErrorMessage(e, ""),
        variant: "error",
      }),
  });

  const set = <K extends keyof DocumentTheme>(k: K, v: DocumentTheme[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  // Validated here so the operator sees it next to the field rather than as a
  // 400 after a round trip. go-pim checks the same thing on save AND again at
  // render — this is the convenience copy, never the enforcement.
  const accentInvalid =
    form.accent_color.trim() !== "" && !isValidAccentColor(form.accent_color);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (accentInvalid) return;
    save.mutate(form);
  };

  if (themeQ.isLoading) return <div className="muted">Loading branding…</div>;
  if (themeQ.isError)
    return (
      <div className="err">
        {apiErrorMessage(themeQ.error, "Could not load branding")}
      </div>
    );

  const accent = isValidAccentColor(form.accent_color)
    ? form.accent_color
    : null;

  return (
    <form onSubmit={onSubmit}>
      <div
        className="card"
        style={{ padding: 12, marginBottom: 16, display: "flex", gap: 10 }}
      >
        <I.Info />
        <div style={{ fontSize: 13 }}>
          <strong>Branding applies to documents issued from now on.</strong>{" "}
          Every document freezes the branding that was in force when it was
          issued, the same way it freezes the issuer block and the payment trail
          — so a receipt someone already downloaded keeps the look it was sent
          with. Nothing here rewrites history.
        </div>
      </div>

      <fieldset style={{ border: 0, padding: 0, marginBottom: 24 }}>
        <legend className="lbl">Accent colour</legend>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <input
            type="color"
            aria-label="Pick an accent colour"
            value={accent ?? "#18181b"}
            onChange={(e) => set("accent_color", e.target.value)}
            style={{ width: 44, height: 34, padding: 2 }}
          />
          <input
            type="text"
            aria-label="Accent colour hex"
            placeholder="#0a7c3f"
            value={form.accent_color}
            onChange={(e) => set("accent_color", e.target.value)}
            style={{ width: 120, fontVariantNumeric: "tabular-nums" }}
          />
          {ACCENT_PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              title={p.label}
              aria-label={`Use ${p.label}`}
              onClick={() => set("accent_color", p.value)}
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                border: "1px solid var(--line)",
                background: p.value,
                cursor: "pointer",
              }}
            />
          ))}
          {form.accent_color !== "" && (
            <button type="button" onClick={() => set("accent_color", "")}>
              Clear
            </button>
          )}
        </div>
        {accentInvalid ? (
          <div className="err" style={{ marginTop: 6, fontSize: 12 }}>
            Must be exactly six hex digits after a hash, like{" "}
            <code>#0a7c3f</code>. Shorthand (<code>#fff</code>) and colour names
            are rejected — this value is written straight into the document's
            stylesheet, so the accepted shape is deliberately narrow.
          </div>
        ) : (
          <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
            Colours the document title, the issuer name and the total rule.
            Leave empty for the default. Body text is never recoloured, so the
            document stays legible whatever you pick.
          </div>
        )}
      </fieldset>

      <fieldset style={{ border: 0, padding: 0, marginBottom: 24 }}>
        <legend className="lbl">Document titles</legend>
        <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
          Leave empty to keep the template's own title. Set per type — a company
          issuing both usually wants a different name on each.
        </div>
        {(
          [
            ["title_receipt", "Receipt", "Payment Receipt"],
            ["title_invoice", "Invoice", "Tax Invoice"],
            [
              "title_payment_confirmation",
              "Payment confirmation",
              "Payment Confirmation",
            ],
          ] as const
        ).map(([key, label, placeholder]) => (
          <label key={key} style={{ display: "block", marginBottom: 8 }}>
            <span className="lbl">{label}</span>
            <input
              type="text"
              value={form[key]}
              maxLength={MAX_THEME_TITLE_LEN}
              placeholder={placeholder}
              onChange={(e) => set(key, e.target.value)}
            />
          </label>
        ))}
      </fieldset>

      <fieldset style={{ border: 0, padding: 0, marginBottom: 24 }}>
        <legend className="lbl">Wording</legend>
        <label style={{ display: "block", marginBottom: 8 }}>
          <span className="lbl">Terms</span>
          <textarea
            rows={4}
            value={form.terms}
            maxLength={MAX_THEME_TERMS_LEN}
            placeholder={
              "1. Panels carry a 25-year warranty.\n2. Installation is scheduled separately."
            }
            onChange={(e) => set("terms", e.target.value)}
          />
          <span className="muted" style={{ fontSize: 12 }}>
            Line breaks are kept, so a numbered list works. Plain text only —
            markup is printed as text, never rendered, because these documents
            are served on a public page.
          </span>
        </label>
        <label style={{ display: "block", marginBottom: 8 }}>
          <span className="lbl">Declaration</span>
          <textarea
            rows={2}
            value={form.declaration}
            maxLength={MAX_THEME_SHORT_TEXT_LEN}
            placeholder="We certify that the particulars given above are true and correct."
            onChange={(e) => set("declaration", e.target.value)}
          />
        </label>
        <label style={{ display: "block", marginBottom: 8 }}>
          <span className="lbl">Signature name</span>
          <input
            type="text"
            value={form.signature_name}
            maxLength={MAX_THEME_TITLE_LEN}
            placeholder="R. Kumar, Director"
            onChange={(e) => set("signature_name", e.target.value)}
          />
          <span className="muted" style={{ fontSize: 12 }}>
            Adds a signature line. Setting this also removes the standing “this
            is a system-generated document, no signature is required” note — a
            document cannot sensibly claim both.
          </span>
        </label>
        <label style={{ display: "block" }}>
          <span className="lbl">Footer note</span>
          <input
            type="text"
            value={form.footer_note}
            maxLength={MAX_THEME_SHORT_TEXT_LEN}
            placeholder="Thank you for your business."
            onChange={(e) => set("footer_note", e.target.value)}
          />
        </label>
      </fieldset>

      <fieldset style={{ border: 0, padding: 0, marginBottom: 24 }}>
        <legend className="lbl">What to print</legend>
        <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
          Presentational only. Anything unticked is still recorded on the
          document — it is simply not printed on it.
        </div>
        {(
          [
            [
              "hide_bank_details",
              "Bank details",
              "The issuer's own account block.",
            ],
            [
              "hide_payment_trail",
              "Payment trail",
              "Which rail carried the money, and between which UPI handles.",
            ],
            [
              "hide_contacts",
              "People",
              "The contacts set to print on this document type, under People.",
            ],
            [
              "hide_verify_link",
              "Verify link",
              "The permanent /r/ link. The link keeps working either way.",
            ],
          ] as const
        ).map(([key, label, hint]) => (
          <label
            key={key}
            style={{
              display: "flex",
              gap: 8,
              alignItems: "start",
              marginBottom: 8,
            }}
          >
            {/* THE ONE INVERSION. Stored as hide_*; shown as "Show …" because
                that is how an operator reads a checkbox. Nowhere else. */}
            <input
              type="checkbox"
              checked={!form[key]}
              onChange={(e) => set(key, !e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span>
              <span>Show {label.toLowerCase()}</span>
              <br />
              <span className="muted" style={{ fontSize: 12 }}>
                {hint}
              </span>
            </span>
          </label>
        ))}
      </fieldset>

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button
          type="submit"
          className="btn-primary"
          disabled={save.isPending || accentInvalid}
        >
          {save.isPending ? "Saving…" : "Save branding"}
        </button>
        {accent && <Pill tone="info">Accent {accent}</Pill>}
        <span className="muted" style={{ fontSize: 12 }}>
          Use Preview to see the result before anything is issued.
        </span>
      </div>
    </form>
  );
}
